import { h } from 'preact';
import { toast } from 'react-toastify';

/**
 * Toast notification service with i18n support
 *
 * Usage:
 * import { showSuccess, showError, showInfo } from '../services/toastService';
 * showSuccess(t(AppConstant.SETTINGS_SAVED_SUCCESS));
 */

const defaultOptions = {
  position: "top-right",
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

/**
 * Show a success toast notification
 * @param {string} message - Translated message to display
 * @param {object} options - Optional toast options
 */
export const showSuccess = (message, options = {}) => {
  toast.success(message, { ...defaultOptions, ...options });
};

/**
 * Show an error toast notification
 * @param {string} message - Translated message to display
 * @param {object} options - Optional toast options
 */
export const showError = (message, options = {}) => {
  toast.error(message, {
    ...defaultOptions,
    autoClose: 5000, // Errors stay longer
    ...options
  });
};

/**
 * Show an info toast notification
 * @param {string} message - Translated message to display
 * @param {object} options - Optional toast options
 */
export const showInfo = (message, options = {}) => {
  toast.info(message, { ...defaultOptions, ...options });
};

/**
 * Show a warning toast notification
 * @param {string} message - Translated message to display
 * @param {object} options - Optional toast options
 */
export const showWarning = (message, options = {}) => {
  toast.warning(message, {
    ...defaultOptions,
    autoClose: 4000,
    ...options
  });
};

// Stable id so repeated calls never stack duplicate update prompts.
const UPDATE_TOAST_ID = 'app-update-available';

/**
 * Show a persistent "new version available" prompt with a reload action.
 * Stays open until the user acts (does not auto-close).
 *
 * @param {object}   params
 * @param {string}   params.message     - Translated prompt text
 * @param {string}   params.actionLabel - Translated reload button label
 * @param {Function} params.onAction    - Invoked when the user clicks reload
 */
export const showUpdatePrompt = ({ message, actionLabel, onAction }) => {
  const content = (
    <div class="toast-update-prompt">
      <span class="toast-update-message">{message}</span>
      <button
        type="button"
        class="toast-update-action"
        onClick={onAction}
        style={{
          marginTop: '0.5rem',
          padding: '0.25rem 0.75rem',
          border: 'none',
          borderRadius: '4px',
          background: '#1976d2',
          color: '#fff',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        {actionLabel}
      </button>
    </div>
  );

  return toast.info(content, {
    ...defaultOptions,
    autoClose: false,
    closeOnClick: false,
    draggable: false,
    toastId: UPDATE_TOAST_ID,
  });
};

/**
 * Dismiss all active toasts
 */
export const dismissAll = () => {
  toast.dismiss();
};
